import { Menu, Search, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface HeaderProps {
  onMenuClick: () => void;
  onSearchClick?: () => void;
}

const Header = ({ onMenuClick, onSearchClick }: HeaderProps) => {
  const { t, i18n } = useTranslation();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  };

  return (
    <header className="bg-white shadow-md sticky top-0 z-50 border-b border-gray-200">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left side - Menu and Logo */}
          <div className="flex items-center space-x-4 rtl:space-x-reverse">
            <button
              onClick={onMenuClick}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Toggle menu"
            >
              <Menu className="h-6 w-6" />
            </button>

            <Link to="/" className="flex items-center space-x-3 rtl:space-x-reverse">
              <div className="h-10 w-10 bg-gradient-to-br from-primary-600 to-primary-800 rounded-lg flex items-center justify-center text-white font-bold text-xl">
                A
              </div>
              <div className="hidden sm:block">
                <div className="text-lg font-bold text-gray-900">
                  Almajdouie Motors
                </div>
                <div className="text-xs text-gray-500">
                  Lead Management Operating System
                </div>
              </div>
            </Link>
          </div>

          {/* Center - Search Button (triggers GlobalSearch modal) */}
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <button
              onClick={onSearchClick}
              className="w-full flex items-center gap-3 px-4 py-2 border border-gray-300 rounded-lg text-gray-400 hover:border-indigo-400 hover:text-gray-600 bg-white hover:bg-gray-50 transition-colors text-sm"
            >
              <Search className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-left">{t('common.search') || 'Search stages, roles, SLAs...'}</span>
              <kbd className="hidden lg:inline-flex items-center gap-1 text-xs bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 text-gray-400 font-mono">
                ⌘K
              </kbd>
            </button>
          </div>

          {/* Right side - Language toggle + mobile search */}
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            {/* Mobile search button */}
            <button
              onClick={onSearchClick}
              className="md:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </button>

            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 rtl:space-x-reverse px-4 py-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
              aria-label={t('common.language')}
            >
              <Globe className="h-5 w-5" />
              <span className="hidden sm:inline text-sm font-medium">
                {i18n.language === 'en' ? 'العربية' : 'English'}
              </span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
